import React, { useEffect, useRef, useState } from 'react';
import { Breadcrumbs } from '../../Home/Breadcrumb';
import DatosGeneralesHabilitacion from './DatosGenerales';
import DocumentacionGeneral from './DocumentacionGeneral';
import PersonalHabilitacionComponent from './Personal';
import { useHabilitacionContratista } from '../../../hooks/useHabilitacionContratista';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useUserLogin } from '../../../hooks/useUserLogin';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  EN_PROCESO_STATUS_ID,
  EN_PROCESO_CARGA_HAB_STATUS,
  HAB_REVISION_STATUS_ID,
  HAB_OBSERVADA_STATUS_ID,
  HAB_APROBADA_STATUS_ID,
  HAB_ACTUALIZACION_STATUS_ID,
} from '../../../constants/constants';
import Confirm from '../../../components/Confirm';
import { Loading } from '../../../components/Loading';
import { toast } from 'react-toastify';
import CalificarHabilitacion from '../../../components/CalificarHabilitacion';

export const HabilitacionContratista = () => {
  const {
    habilitacionSelected,
    habilitacionInitial,
    buscarHabilitacion,
    validateHabilitacion,
    validarCalificarHabilitacion,
    sendHabilitacion,
    resetHabilitacion,
    handleHabilitacionSelected,
  } = useHabilitacionContratista();

  const {
    isContratistaTipoUsuario,
    isEmpresaGestoraTipoUsuario,
    isEmpresaSupervisoraTipoUsuario,
    getUsuario,
  } = useUserLogin();

  const isContratista    = isContratistaTipoUsuario();
  const isEmpresaGestora = isEmpresaGestoraTipoUsuario();
  const isRevisor        = isEmpresaGestora || isEmpresaSupervisoraTipoUsuario();

  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('mode') === 'view';
  const datosGeneralesRef = useRef<any>();

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  const [showModalEnviar, setShowModalEnviar] = useState(false);
  const [showModalCalificar, setShowModalCalificar] = useState(false);
  const toggleEnviar = () => setShowModalEnviar(!showModalEnviar);
  const toggleCalificar = () => setShowModalCalificar(!showModalCalificar);

  useEffect(() => {
    if (id) {
      buscarHabilitacion(id);
    } else {
      const usuario = getUsuario();
      handleHabilitacionSelected({
        ...habilitacionInitial,
        idContratista:           usuario?.idContratista || 0,
        nombreContratista:       usuario?.contratista || '',
        idUsuarioCreacion:       usuario?.id || 0,
        nombreUsuarioCreacion:   usuario?.nombre || '',
        apellidoUsuarioCreacion: usuario?.apellido || '',
        fechaCreacion:           new Date().toISOString(),
        idTipoEstado:            EN_PROCESO_STATUS_ID,
        nombreEstado:            EN_PROCESO_CARGA_HAB_STATUS,
      });
      setIsEdit(isContratista);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (habilitacionSelected.id !== 0) {
      const estadoActual = habilitacionSelected.idTipoEstado;
      const puedeEditar =
        !isViewMode &&
        isContratista &&
        [EN_PROCESO_STATUS_ID, HAB_OBSERVADA_STATUS_ID, HAB_ACTUALIZACION_STATUS_ID].includes(
          estadoActual,
        );
      setIsEdit(puedeEditar);
      setIsLoading(false);
    }
  }, [habilitacionSelected, isContratista]);

  const estadoActual    = habilitacionSelected.idTipoEstado;
  const puedeEnviar     = isEdit;
  // En "Aprobada" el contratista puede editar los acordeones; al agregar documentación/personal
  // el backend transiciona automáticamente a "Actualizacion" (donde recién aparece "Enviar").
  const puedeEditarAcordeones =
    !isViewMode &&
    isContratista &&
    [
      EN_PROCESO_STATUS_ID,
      HAB_OBSERVADA_STATUS_ID,
      HAB_ACTUALIZACION_STATUS_ID,
      HAB_APROBADA_STATUS_ID,
    ].includes(estadoActual);
  const puedeCalificar  = !isViewMode && isRevisor && estadoActual === HAB_REVISION_STATUS_ID;
  const puedeFinalizar  = !isViewMode && isEmpresaGestora && estadoActual === HAB_APROBADA_STATUS_ID;

  const handleGuardar = () => {
    if (datosGeneralesRef.current) {
      datosGeneralesRef.current.handleSubmit();
    }
  };

  const handleEnviar = async () => {
    if (!habilitacionSelected.idEmpresaGestora) {
      toast.error(
        'Debe seleccionar una Empresa Gestora antes de enviar.',
      );
      return;
    }
    try {
      const result = await validateHabilitacion(
        habilitacionSelected.id,
      );
      if (!result.valid) {
        result.errores.forEach((error: string) => toast.error(error));
        return;
      }
    } catch (error) {
      toast.error('Error al validar la habilitación.');
      return;
    }
    toggleEnviar();
  };

  const handleEnviarDirecto = () => {
    setShowModalEnviar(true);
  };

  const handleConfirmEnviar = async () => {
    try {
      const response = await sendHabilitacion(
        habilitacionSelected.id,
      );
      if (response) {
        toast.success('Habilitación enviada correctamente');
        navigate('/habilitacion-contratista');
      }
    } catch (error) {
      toast.error('Error al enviar la habilitación');
    }
    setShowModalEnviar(false);
  };

  const handleCalificar = async () => {
    const haySinRevisar = await validarCalificarHabilitacion(
      habilitacionSelected.id,
    );
    if (haySinRevisar !== undefined && !haySinRevisar) {
      setShowModalCalificar(true);
    }
  };

  const handleCalificarSaved = () => {
    if (id) buscarHabilitacion(id);
  };

  const handleCancelar = () => {
    navigate(-1);
  };

  const isNuevo = !id;
  const pageTitle = isNuevo
    ? 'Alta Datos Contratista'
    : isEdit
      ? 'Edición Datos Contratista'
      : 'Vista Datos Contratista';

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <ToastContainer />
      <div className="content-page">
        <div className="content">
          <div className="container-fluid">
            <Breadcrumbs
              title={pageTitle}
              nameFirst={'Inicio'}
              urlFirst={'/'}
            />
            <Confirm
              isOpen={showModalEnviar}
              toggle={toggleEnviar}
              title="Confirmar"
              yesFn={handleConfirmEnviar}
            >
              <div className="form-horizontal">
                <div className="row">
                  <p>
                    Está seguro que desea enviar la habilitación al
                    siguiente estado?
                  </p>
                </div>
              </div>
            </Confirm>

            <CalificarHabilitacion
              isOpen={showModalCalificar}
              toggle={toggleCalificar}
              habilitacionId={habilitacionSelected.id}
              idEstadoTemporalActual={habilitacionSelected.idEstadoTemporal}
              onSaved={handleCalificarSaved}
            />

            <DatosGeneralesHabilitacion
              ref={datosGeneralesRef}
              isDisabled={!isEdit}
              handleCancelar={handleCancelar}
            />

            {habilitacionSelected.id !== 0 && (
              <>
                <DocumentacionGeneral
                  isEdit={puedeEditarAcordeones}
                  isDisabled={!puedeEditarAcordeones}
                  soloLectura={isViewMode}
                />
                <PersonalHabilitacionComponent
                  isEdit={puedeEditarAcordeones}
                  isDisabled={!puedeEditarAcordeones}
                  puedeCalificar={puedeCalificar}
                />
              </>
            )}

            <br />
            <div className="text-center">
              <button
                className="btn btn-secondary mx-2"
                onClick={handleCancelar}
              >
                Volver
              </button>
              {puedeEnviar && (
                <button
                  className="btn btn-primary mx-2"
                  onClick={handleEnviar}
                >
                  Enviar
                </button>
              )}
              {puedeCalificar && (
                <button
                  className="btn btn-info mx-2"
                  onClick={handleCalificar}
                >
                  Calificar
                </button>
              )}
              {puedeFinalizar && (
                <button
                  className="btn btn-success mx-2"
                  onClick={handleEnviarDirecto}
                >
                  Finalizar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
